import React from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

const parseSaved = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return []; }
};

const CATEGORIES = [
  "documents", "electronics", "clothing", "food",
  "medicines", "cosmetics", "gifts", "books",
  "toys", "accessories", "sports", "homeware",
];

const PRESET_RESTRICTIONS = [
  "weapons", "drugs", "liquids", "perishables",
  "animals", "currency", "jewellery", "hazmat",
];

function BaggageDetails({
  baggageData = {},
  documentData = {},
  pickupData = {},
  changedPaths = [],
  listing = {},
}) {
  const { t } = useTranslation();

  const baggage = baggageData || {};
  const docData = documentData || {};
  const pickup = pickupData || {};

  const categoriesWillCarry = parseSaved(baggage.categoriesWillCarry);
  const restrictionsWillNotCarry = parseSaved(baggage.restrictionsWillNotCarry);

  const timezone =
    docData.departureTimezone ||
    docData.timezone ||
    listing.departureTimezone ||
    listing.timezone ||
    listing.documentDetails?.departureTimezone ||
    listing.documentDetails?.timezone ||
    (listing.fromPlace ? helpers.deriveTimezone(listing.fromPlace) : null);

  const formattedTz = timezone ? helpers.formatTimezone(timezone) : "";

  return (
    <Row className="mt-4">
      <Col md="6" className="d-flex flex-column">
        <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
          <CardBody className="py-2 px-3 h-100">
            <h4 className="heading-md text-muted mb-2">
              {t("page.orders.baggageDetails.title", "Baggage Details")}
            </h4>

            <p className="heading-sm mb-0">
              <strong>{t("page.orders.baggageDetails.weight", "Weight")}:</strong>{" "}
              <span className={helpers.getHighlightClass("baggageDetails.weight", changedPaths)}>
                {baggage.weight || "—"}
              </span>
            </p>
            <p className="heading-sm mb-1">
              <strong>{t("page.orders.baggageDetails.size", "Size")}:</strong>{" "}
              <span className={helpers.getHighlightClass("baggageDetails.size", changedPaths)}>
                {baggage.size || "—"}
              </span>
            </p>

            {baggage.itemCanBeTaken && (
              <p className="heading-sm mb-0 mt-1">
                <strong>{t("page.orders.baggageDetails.itemsAllowed", "Items Allowed")}:</strong>{" "}
                <span className={helpers.getHighlightClass("baggageDetails.itemCanBeTaken", changedPaths)}>
                  {baggage.itemCanBeTaken}
                </span>
              </p>
            )}

            {categoriesWillCarry.length > 0 && (
              <div className="mt-2 mb-2">
                <p className="heading-sm mb-1">
                  <strong>{t("page.create-listing.baggageDetails.categoriesTitle", "What I Can Carry")}:</strong>
                </p>
                <div
                  className={helpers.getHighlightClass("baggageDetails.categoriesWillCarry", changedPaths)}
                  style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                >
                  {categoriesWillCarry.map((cat) => {
                    const key = CATEGORIES.find(c => c === String(cat).toLowerCase());
                    return (
                      <span
                        key={cat}
                        style={{
                          background: "#EEF4FB",
                          color: "#185FA5",
                          border: "1px solid #185FA5",
                          borderRadius: "6px",
                          fontWeight: 500,
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          display: "inline-block",
                        }}
                      >
                        {key ? t(`page.create-listing.baggageDetails.categories.${key}`) : cat}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {restrictionsWillNotCarry.length > 0 && (
              <div className="mt-2 mb-2">
                <p className="heading-sm mb-1">
                  <strong>{t("page.create-listing.baggageDetails.restrictionsTitle", "What I Will NOT Carry")}:</strong>
                </p>
                <div
                  className={helpers.getHighlightClass("baggageDetails.restrictionsWillNotCarry", changedPaths)}
                  style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                >
                  {restrictionsWillNotCarry.map((r) => {
                    const key = PRESET_RESTRICTIONS.find(x => x === String(r).toLowerCase());
                    return (
                      <span
                        key={r}
                        style={{
                          background: "#fff5f5",
                          color: "#dc3545",
                          border: "1px solid #f5c6cb",
                          borderRadius: "999px",
                          fontWeight: 400,
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          display: "inline-block",
                        }}
                      >
                        ✕ {key ? t(`page.create-listing.baggageDetails.restrictions.${key}`) : r}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
          <CardBody className="py-2 px-3 h-100">
            <h4 className="heading-md text-muted mb-2">
              {t("page.create-listing.baggageDetails.handoverPreference", "Handover Preference")}
            </h4>

            <p className="heading-sm mb-0">
              <strong>{t("page.create-listing.baggageDetails.handoverPreference", "Preference")}:</strong>{" "}
              <span className={helpers.getHighlightClass("pickUpDetails.handoverPreference", changedPaths)}>
                {pickup.handoverPreference || "—"}
              </span>
            </p>

            {pickup.meetingPointHint && (
              <p className="heading-sm mb-0 mt-1">
                <strong>{t("page.create-listing.baggageDetails.meetingPointHint", "Meeting Point")}:</strong>{" "}
                <span className={helpers.getHighlightClass("pickUpDetails.meetingPointHint", changedPaths)}>
                  {pickup.meetingPointHint}
                </span>
              </p>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col md="6">
        <Card className="shadow border-0 h-100">
          <CardBody className="py-2 px-3 h-100">
            <h4 className="heading-md text-muted mb-2">
              {t("page.create-listing.documentDetails.title", "Document Details")}
            </h4>

            <p className="description mb-2">
              <strong>{t("page.create-listing.documentDetails.travelType", "Travel Type")}:</strong>{" "}
              <span className={helpers.getHighlightClass("documentDetails.travelType", changedPaths)}>
                {docData.travelType || "—"}
              </span>
            </p>

            <p className="description mb-2">
              <strong>{t("page.create-listing.documentDetails.pnrNumber", "PNR Number")}:</strong>{" "}
              <span className={helpers.getHighlightClass("documentDetails.pnrNumber", changedPaths)}>
                {docData.pnrNumber || "—"}
              </span>
            </p>

            <p className="description mb-2">
              <strong>{t("page.create-listing.documentDetails.travelTime", "Departure Time")}:</strong>{" "}
              <span className={helpers.getHighlightClass("documentDetails.travelTime", changedPaths)}>
                {helpers.formatTime(docData.travelTime)}
              </span>
            </p>

            <p className="description mb-2">
              <strong>{t("page.create-listing.documentDetails.timezone", "Time Zone")}:</strong>{" "}
              <span className={helpers.getHighlightClass(["departureTimezone", "documentDetails.departureTimezone"], changedPaths)}>
                {formattedTz || "—"}
              </span>
            </p>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

export default BaggageDetails;
